import { forwardRef, type SelectHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  options: readonly SelectOption[];
  placeholder?: string;
  hasError?: boolean;
}

/** A styled native `<select>` — keyboard, mobile and RTL behaviour for free. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(({ className, options, placeholder, hasError = false, ...props }, ref) => (
  <select
    ref={ref}
    aria-invalid={hasError || undefined}
    className={cn(
      'h-9 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
      'disabled:cursor-not-allowed disabled:opacity-50',
      hasError && 'border-destructive focus-visible:ring-destructive',
      className,
    )}
    {...props}
  >
    {placeholder ? <option value="">{placeholder}</option> : null}
    {options.map((option) => (
      <option
        key={option.value}
        value={option.value}
        disabled={option.disabled}
      >
        {option.label}
      </option>
    ))}
  </select>
));

Select.displayName = 'Select';
